import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, RefreshCw, ArrowRight, MailCheck } from "lucide-react";
import { authResendVerify, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function EmailSent() {
  const loc = useLocation();
  const nav = useNavigate();
  const email = loc.state?.email || "";
  const role = loc.state?.role || "company";
  const [devToken, setDevToken] = useState(loc.state?.dev_verify_token || "");
  const [verifyPath, setVerifyPath] = useState(loc.state?.verify_path || "");
  const [resending, setResending] = useState(false);

  const resend = async () => {
    if (!email) return;
    setResending(true);
    try {
      const r = await authResendVerify(email);
      if (r.dev_verify_token) {
        setDevToken(r.dev_verify_token);
        setVerifyPath(r.verify_path);
      }
      toast.success(r.message || "Verification link generated.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not resend link."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="email-sent-page" className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#00ff88]/10 border border-[#00ff88]/30 flex items-center justify-center mb-5">
          <MailCheck className="text-[#00ff88]" size={32} />
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-2">Check your email</h1>
        <p className="text-sm text-[#a0a0b0]">
          We sent a verification link to{" "}
          <span className="text-white font-mono">{email || "your inbox"}</span>.
          Click it to confirm your account.
        </p>

        {devToken && (
          <div data-testid="email-sent-dev-link" className="mt-6 cc-surface rounded-xl p-4 border border-[#00ff88]/20 text-left">
            <div className="cc-overline text-[#00ff88] mb-1">Dev mode · no email provider</div>
            <Link
              to={verifyPath}
              data-testid="email-sent-verify-link"
              className="text-xs text-[#00ff88] font-mono break-all hover:underline"
            >
              {verifyPath}
            </Link>
          </div>
        )}

        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            data-testid="email-sent-resend"
            onClick={resend}
            disabled={resending || !email}
            className="px-4 py-2 text-sm border border-[#15152a] bg-[#0a0a14] text-white rounded-lg hover:border-[#1f1f3a] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCw size={14} className={resending ? "animate-spin" : ""} /> Resend link
          </button>
          <button
            data-testid="email-sent-skip"
            onClick={() => nav(role === "creator" ? "/settings" : "/dashboard", { replace: true })}
            className="px-4 py-2 text-sm cc-btn-primary rounded-lg inline-flex items-center gap-2"
          >
            Continue <ArrowRight size={14} />
          </button>
        </div>

        <div className="text-[11px] text-[#606070] mt-6">
          Wrong address?{" "}
          <Link to="/signup" className="hover:text-white">Create a new account</Link>
        </div>
      </div>
    </div>
  );
}
